import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Categories() {
  const [activeCategory, setActiveCategory] = useState(categories[0].id);

  const articlesRef = useRef<HTMLDivElement>(null);

  const selectedCategory = categories.find((c) => c.id === activeCategory);
  const filteredArticles = articles.filter((a) => a.categoryId === activeCategory);

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    articlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="分类浏览 - 开云平台"
        description="探索开云平台的各类内容分类，包括平台入门、功能指南、使用场景、更新动态等，快速找到您需要的资源。"
        keywords="开云分类,开云平台入门,开云功能指南,开云使用场景"
        canonicalUrl="https://kaiyun.example.com/categories"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '分类浏览' }]} />
          </div>

          <section className="pb-8">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">分类浏览</h1>
              <p className="text-sm text-foreground-600 mb-8">选择感兴趣的分类，探索相关内容</p>

              {/* Category Tabs */}
              <div className="flex flex-wrap gap-3 mb-12">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-700 border border-background-200/70 hover:border-primary-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Selected Category Info */}
              {selectedCategory && (
                <div ref={articlesRef} className="mb-10 p-6 rounded-xl bg-accent-50 border border-accent-200/50">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                      <i className={`${selectedCategory.icon} text-lg text-accent-600`}></i>
                    </div>
                    <h2 className="text-xl font-heading font-semibold text-foreground-900">{selectedCategory.name}</h2>
                  </div>
                  <p className="text-sm text-foreground-600 ml-13">{selectedCategory.description}</p>
                </div>
              )}

              {/* Articles Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.author}</span>
                      </div>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>

              {filteredArticles.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}